import { useRemoteSelect } from '@neo/hooks/use-remote-select'
import { Select, type SelectOption } from '@neo/primitives/select'
import { AgentState } from 'v2/pages/flow/types'
import { useFlowOptionsByIdsQuery } from 'v2/pages/settings/queries'
import { settingsService } from 'v2/shared/services/settings-service'
import { getSettingsQueryKeys } from 'v2/shared/utils/query-key-management'
import type { RFlowOption, RFlowOptionsPayload } from 'v2/types/api/settings'

const PAGE_SIZE = 20 // /phlo/options default & true page size

/** Which flow field the select stores: the id, or the trigger url (event callbacks). */
export type FlowValueKey = 'id' | 'url'

/** The uuid is the last path segment of a flow trigger url; a bare uuid passes through. */
export const uuidFromFlowUrl = (value: string) => value.split('/').filter(Boolean).at(-1) ?? value

export const flowToOption = (
  flow: RFlowOption,
  getDisabledReason?: (flow: RFlowOption) => string | undefined,
  valueKey: FlowValueKey = 'id',
): SelectOption => {
  const disabledReason = getDisabledReason?.(flow)
  return {
    label: flow.name,
    value: flow[valueKey],
    disabled: !!disabledReason,
    tooltipContent: disabledReason,
  }
}

export const extractFlowOptions = (
  data: unknown,
  getDisabledReason?: (flow: RFlowOption) => string | undefined,
  valueKey: FlowValueKey = 'id',
) => {
  const payload = data as RFlowOptionsPayload
  return {
    options: payload.objects.map((flow) => flowToOption(flow, getDisabledReason, valueKey)),
    hasNextPage: payload.page * payload.limit < payload.total,
    nextPage: payload.page + 1,
  }
}

interface FlowSelectProps {
  value?: string | string[]
  onChange?: (value: string | string[]) => void
  placeholder?: string
  className?: string
  disabled?: boolean
  label?: string
  error?: React.ReactNode
  helperText?: string
  multiple?: boolean
  clearable?: boolean
  /** Server-side phlo_type filter (e.g. StartNodeTypes.start_api_request) */
  phloType?: string
  /** Only ACTIVE flows (server-side) — the old client-side flowFilterActive */
  activeOnly?: boolean
  valueKey?: FlowValueKey
  /** Ineligible flows are DISABLED (with this reason as tooltip), never hidden —
   * client-side filtering of a server page makes items silently unreachable. */
  getDisabledReason?: (flow: RFlowOption) => string | undefined
  'data-testid'?: string
}

export const FlowSelect = ({
  value,
  onChange,
  placeholder = 'Select agent flow...',
  className,
  disabled,
  label,
  error,
  helperText,
  multiple = false,
  clearable = false,
  phloType,
  activeOnly = false,
  valueKey = 'id',
  getDisabledReason,
  'data-testid': dataTestId = 'select-flow',
}: FlowSelectProps) => {
  const serverParams = {
    ...(phloType ? { phlo_type: phloType } : {}),
    ...(activeOnly ? { status: AgentState.ACTIVE } : {}),
  }

  const optionExtractor = (data: unknown) => extractFlowOptions(data, getDisabledReason, valueKey)

  const selectedValue = !multiple && typeof value === 'string' && value ? value : undefined

  const {
    options,
    isLoading,
    error: fetchError,
    setSearchTerm,
    setIsOpen,
    loadMore,
  } = useRemoteSelect({
    queryKey: getSettingsQueryKeys().flowOptions(
      phloType,
      activeOnly ? AgentState.ACTIVE : undefined,
    ),
    fetchService: (params) => settingsService.listFlowOptions({ ...params, ...serverParams }),
    optionExtractor,
    searchKey: 'name', // server-side ILIKE on flow name
    pageSize: PAGE_SIZE,
    selectedValue,
    // uuid point-lookup keeps an out-of-page saved flow's label rendering
    // without pulling the full-graph GET /phlo/{uuid}
    fetchResource: (id) => settingsService.listFlowOptions({ uuid: uuidFromFlowUrl(String(id)) }),
    optionFromResource: (data) => {
      const flow = (data as RFlowOptionsPayload).objects[0]
      return flow
        ? flowToOption(flow, getDisabledReason, valueKey)
        : { label: String(selectedValue), value: String(selectedValue) }
    },
  })

  // Keyed on the selection alone, so loading more pages never refetches or flickers the chips.
  const { data: savedFlows = [] } = useFlowOptionsByIdsQuery(
    multiple && Array.isArray(value) ? value.map(uuidFromFlowUrl) : [],
  )
  const savedOptions = savedFlows.map((flow) => flowToOption(flow, getDisabledReason, valueKey))
  const allOptions = savedOptions.length
    ? [...savedOptions, ...options.filter((o) => !savedOptions.some((s) => s.value === o.value))]
    : options

  return (
    <Select
      value={value}
      onChange={onChange}
      options={allOptions}
      placeholder={placeholder}
      className={className}
      disabled={disabled}
      label={label}
      error={fetchError ? 'Unable to load agent flows' : error}
      helperText={helperText}
      loading={isLoading}
      searchable
      clearable={clearable}
      multiple={multiple}
      onSearchChange={setSearchTerm}
      onLoadMore={loadMore}
      onOpenChange={setIsOpen}
      data-testid={dataTestId}
    />
  )
}

/** FlowSelect limited to ACTIVE flows: the shape every "attach an agent" form uses. */
export const ActiveFlowSelect = (props: React.ComponentProps<typeof FlowSelect>) => (
  <FlowSelect {...props} activeOnly />
)

/** Multi-select over flows, hydrating already-saved ids that sit beyond the loaded pages. */
export const MultiFlowSelect = (props: React.ComponentProps<typeof FlowSelect>) => (
  <FlowSelect {...props} multiple />
)
